/**
 * A board whose first page arrived with its saved view page, and whose later pages, axis pages and
 * moves are asked of that view's own board endpoints (github/views#177). Shared by every saved view
 * surface that serves the board from the server, so each one binds only its paths and cache key.
 *
 * Both axes carry their own display material, so the headings read the payload rather than
 * anything gathered from the cards in a bucket.
 *
 * A move does cost a request, and this is where that request lives: the transport is the
 * adapter's, the cache lifecycle around it is `SavedViewBoard`'s.
 */

import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'

import {
  axisLabels,
  type BoardAxisLabels,
  type BoardInitialPayload,
  type BoardPayloadItem,
} from '../components/board/issues/board-payload'
import type {IssuesBoardMetadata} from '../components/board/issues/board-metadata'
import type {BoardDeferredFields} from '../components/board/issues/deferred-payload'
import {isBoardAxisPage, isBoardCellPage, toBoardAxisPage} from '../components/board/issues/paging-payload'
import {toBoardData} from '../components/board/to-board-data'
import type {
  SavedViewsBoardAdapter,
  SavedViewsBoardAxisPage,
  SavedViewsBoardAxisPageRequest,
  SavedViewsBoardCellPage,
  SavedViewsBoardCellPageRequest,
  SavedViewsBoardOptions,
  SavedViewsBoardPayload,
} from '../components/board/types'
import {postBoardUpdate} from './board-update-api'
import {createSavedViewDeferredFieldsSource} from './create-saved-view-deferred-fields-source'

interface SavedViewBoardEndpoints {
  /** Names the board's requests in the route query metrics. */
  routeId: string
  /** The view's endpoint for the next page of one cell. */
  cellItemsPath: string
  /** The view's endpoint for the next page of one axis. */
  boardAxisPath: string
}

export function createSavedViewBoardAdapter({
  board,
  key,
  routeId,
  cellItemsPath,
  boardAxisPath,
  deferredFields,
}: SavedViewBoardEndpoints & {
  board: BoardInitialPayload
  /** Includes the response the board came from, so a new response keys a new board. */
  key: SavedViewsBoardAdapter<BoardPayloadItem, IssuesBoardMetadata, BoardAxisLabels>['key']
  deferredFields?: {
    pathname: string
    /** Identifies the view, excluding the board response and page. */
    scope: ReadonlyArray<string | number>
  }
}): SavedViewsBoardAdapter<BoardPayloadItem, IssuesBoardMetadata, BoardAxisLabels, BoardDeferredFields> {
  const payload = toBoardPayload(board)

  return {
    key,
    deferredFieldsSource: deferredFields
      ? createSavedViewDeferredFieldsSource({...deferredFields, routeId})
      : undefined,
    readInitialBoardPayload: () => payload,
    fetchInitialBoardPayload: async () => payload,
    // Item-scoped and surface-agnostic, so nothing about the view reaches the request: the
    // card's global id gives the server its repository, and the repository gives the owner
    // that resolves a custom field.
    moveBoardItem: postBoardUpdate,
    fetchCellPage: request => fetchCellPage(request, {routeId, pathname: cellItemsPath, board}),
    fetchAxisPage: request => fetchAxisPage(request, {routeId, pathname: boardAxisPath, board}),
    appendAxisMetadata: (metadata, axisPage) => {
      const labels = new Map(axisPage.axis === 'columns' ? metadata.columnLabels : metadata.rowLabels)
      for (const [labelKey, label] of axisPage.metadata) labels.set(labelKey, label)

      return axisPage.axis === 'columns' ? {...metadata, columnLabels: labels} : {...metadata, rowLabels: labels}
    },
  }
}

interface PageTarget {
  routeId: string
  pathname: string
  board: BoardInitialPayload
}

/**
 * Every part of the request comes from the board that was drawn, not the URL: the server falls
 * back to the view's stored arrangement for anything unstated, which can differ from what is on
 * screen.
 */
async function fetchCellPage(
  {columnKey, rowKey, cursor, query, signal}: SavedViewsBoardCellPageRequest & SavedViewsBoardOptions,
  {routeId, pathname, board}: PageTarget,
): Promise<SavedViewsBoardCellPage<BoardPayloadItem>> {
  const swimlaned = board.rowField != null
  // An unswimlaned board is answered under the reserved blank key.
  const expectedRowKey = swimlaned ? rowKey : ''

  // eslint-disable-next-line @github-ui/github-monorepo/require-query-schema -- The board cell endpoint returns plain JSON validated by `isBoardCellPage`, not a Protobuf payload.
  const json = await queryFnFetch<unknown>({
    routeId,
    queryName: 'boardCellPage',
    queryDeps: {
      pathname,
      searchParams: {
        // Not the query reassembled from the URL and this browser's cached view, which can be
        // stale: a differing `sort:` resumes the cursor through a different ordering.
        q: board.query ?? query,
        // Stated even when unswimlaned, since naming nothing falls back to the stored arrangement.
        group_by: board.columnField ?? '',
        swimlane_by: board.rowField ?? '',
        column_key: columnKey,
        row_key: swimlaned ? rowKey : null,
        items_after: cursor,
        // Left unsaid, a later page is sized by the search default and pages in a different
        // stride than the cell loaded in.
        items_page_size: board.pageSize == null ? null : String(board.pageSize),
      },
      init: {signal},
    },
  })

  if (!isBoardCellPage(json)) throw new Error('board cell page failed validation')
  // A cursor carries sort values, not a cell, so nothing downstream could notice a page that
  // answered for the wrong pair.
  if (json.columnKey !== columnKey || json.rowKey !== expectedRowKey) {
    throw new Error('board cell page answered for a different cell')
  }

  return json
}

async function fetchAxisPage(
  {axis, cursor, pinnedKeys, visiblePinnedKeys, query, signal}: SavedViewsBoardAxisPageRequest & SavedViewsBoardOptions,
  {routeId, pathname, board}: PageTarget,
): Promise<SavedViewsBoardAxisPage<BoardPayloadItem, BoardAxisLabels>> {
  // eslint-disable-next-line @github-ui/github-monorepo/require-query-schema -- The board axis endpoint returns plain JSON validated by `isBoardAxisPage`, not a Protobuf payload.
  const json = await queryFnFetch<unknown>({
    routeId,
    queryName: 'boardAxisPage',
    queryDeps: {
      pathname,
      init: {
        method: 'POST',
        signal,
        body: {
          axis,
          after: cursor,
          pinned_keys: pinnedKeys,
          // Left out, the server spreads cards over every pinned lane and most drawn cells
          // come back with counts only.
          ...(visiblePinnedKeys === undefined ? {} : {item_keys: lanesToLoad(visiblePinnedKeys, pinnedKeys)}),
          q: board.query ?? query,
          group_by: board.columnField ?? '',
          swimlane_by: board.rowField ?? '',
          items_page_size: board.pageSize ?? null,
        },
      },
    },
  })

  if (!isBoardAxisPage(json)) throw new Error('board axis page failed validation')
  if (json.axis !== axis) throw new Error('board axis page answered for a different axis')

  const fieldLabel = json.axis === 'columns' ? board.columnFieldLabel : board.rowFieldLabel
  return toBoardAxisPage(json, fieldLabel)
}

/**
 * The drawn lanes to load cards for. The server answers 422 for a key outside `pinned_keys`, and
 * gives cards to the first lanes its item budget fits.
 */
function lanesToLoad(visible: readonly string[], pinned: readonly string[]): string[] {
  const pinnedKeys = new Set(pinned)

  return visible.filter(laneKey => pinnedKeys.has(laneKey))
}

function toBoardPayload(board: BoardInitialPayload): SavedViewsBoardPayload<BoardPayloadItem, IssuesBoardMetadata> {
  return {
    boardData: toBoardData(board),
    metadata: {
      columnLabels: axisLabels(board.columns),
      rowLabels: axisLabels(board.rows),
      pageInfo: undefined,
      error: undefined,
    },
  }
}
